<template>
  <div class="report-page">
    <div class="filter-card">
      <div class="fc-row fc-row-primary fc-row-with-search">
        <div class="field">
          <label class="field-lbl">ช่วงวันที่</label>
          <date-picker
            v-model="dateRange"
            type="date"
            range
            format="DD MMM YY"
            :shortcuts="dateRangeShortcuts"
            :disabled-date="notAfterToday"
            placeholder="เลือกวันที่ต้องการค้นหา"
            input-class="form-control form-control-sm field-input-native"
          ></date-picker>
          <small v-if="moreThanOneDay" class="text-muted">
            การขอรายงานที่มีช่วงเวลามากกว่า 1 วัน รายงานจะถูกจัดส่งให้ทางอีเมลเท่านั้น
          </small>
        </div>

        <div class="field">
          <label class="field-lbl">สถานะใบเสร็จ</label>
          <b-form-select
            v-model="receiptStatus"
            :options="receiptStatusOpt"
            size="sm"
            class="field-input-native"
          ></b-form-select>
        </div>

        <div class="field">
          <label class="field-lbl"><i class="fas fa-search"></i> ค้นหา</label>
          <b-form-input
            v-model.trim="searchTable"
            type="search"
            size="sm"
            class="field-input-native"
            placeholder="ชื่อคนไข้, DN หรือเลขที่ใบเสร็จ"
          />
        </div>

        <b-button
          v-if="moreThanOneDay"
          variant="primary"
          class="btn-primary-lg"
          @click="sendEmail"
        >
          ส่งรายงานผ่าน Email
        </b-button>
        <b-button
          v-else
          variant="primary"
          class="btn-primary-lg"
          @click="fetchReport"
        >
          แสดงข้อมูล
        </b-button>
      </div>
    </div>
    <div
      v-if="!moreThanOneDay"
      id="report-elements"
      ref="fullscreenRoot"
      :class="{ 'my-fullscreen': isFullscreen }"
    >
      <div class="my-fullscreen-bar">
        <span class="title">{{ reportTitle }}</span>
        <div class="meta">
          <span v-if="dateRange[0] && dateRange[1]">
            วันที่: {{ formatDate(dateRange[0]) }} – {{ formatDate(dateRange[1]) }}
          </span>
          <span v-if="branchInfo && branchInfo.name">
            สาขา: {{ branchInfo.name }}
          </span>
        </div>
        <span class="spacer"></span>
        <b-button
          size="sm"
          variant="outline-secondary"
          :title="fullscreenLabel"
          :aria-label="fullscreenLabel"
          @click="toggleFullscreen"
        >
          <i class="fas fa-times"></i>
        </b-button>
      </div>
      <div class="toolbar">
        <b-pagination
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          size="sm"
          first-number
          last-number
          pills
          limit="10"
          class="mb-0"
        ></b-pagination>

        <div class="toolbar-right">
          <b-form-checkbox v-model="showAllPaymentMethods" switch size="sm" class="mb-0">
            แสดงวิธีชำระทั้งหมด
          </b-form-checkbox>
          <b-dropdown size="sm" variant="outline-success" right>
            <template #button-content>
              <i class="fas fa-download"></i>
            </template>
            <b-dropdown-item @click="excelExport('ReceiptIncomeTable')">
              <i class="fas fa-file-excel mr-2"></i>Excel
            </b-dropdown-item>
            <b-dropdown-item @click="pdfExport('ReceiptIncomeTable')">
              <i class="fas fa-file-pdf mr-2"></i>PDF
            </b-dropdown-item>
          </b-dropdown>
          <b-button
            size="sm"
            :variant="fullscreenButtonVariant"
            :title="fullscreenLabel"
            :aria-label="fullscreenLabel"
            @click="toggleFullscreen"
          >
            <i class="fas" :class="fullscreenIcon"></i>
          </b-button>
        </div>
      </div>

      <b-table
        id="ReceiptIncomeTable"
        ref="ReceiptIncomeTable"
        class="my-0 sticky-footer report-table"
        small
        sort-icon-left
        hover
        head-variant="light"
        :sticky-header="tableHeight"
        :busy="tableBusy"
        :items="filteredReceiptsList"
        :fields="dynamicFields"
        sort-by="creationDt"
        v-model="visibleRows"
        :sort-desc="false"
        :responsive="true"
        caption="Double click เพื่อดูใบเสร็จ"
        show-empty
        :per-page="perPage"
        :current-page="currentPage"
        @row-dblclicked="openReceipt"
      >
        <template
          v-for="paymentMethod in paymentMethodList"
          #[`head(receiptPayment.${paymentMethod.id}.amount)`]
        >
          <ProcessingFeeTooltip
            v-if="paymentMethod.processingFee"
            :key="`header-${paymentMethod.id}`"
            :label="paymentMethod.name"
            :popover-id="`processing-fee-${paymentMethod.id}`"
            :popover-title="`${paymentMethod.name} Processing Fee`"
            :processingFee="paymentMethod.processingFee"
          />
          <span v-else :key="`header-${paymentMethod.id}`">{{
            paymentMethod.name
          }}</span>
        </template>
        <template #cell(index)="row">{{ row.item.row }}.</template>
        <template #cell(creationDt)="row">{{ formatDate(row.value) }}</template>
        <template #cell(time)="row">{{
          formatTime(row.item.creationDt)
        }}</template>
        <template #cell(status)="row"
          ><span
            v-if="row.item.cancelFlag"
            v-b-popover.hover.bottomleft="cancelPopoverConfig(row.item)"
          >
            ยกเลิก
          </span></template
        >
        <template #custom-foot>
          <b-tr variant="secondary" class="text-bold text-right">
            <td v-for="field in dynamicFields" :key="field.key">
              {{ formatNumber(sum(field.key)) }}
            </td>
          </b-tr>
        </template>
        <template #empty="">
          <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
        </template>
      </b-table>
    </div>

    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { eventBus } from "@/main";
import ProcessingFeeTooltip from "@/components/tooltip/ProcessingFee";

import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import moment from "moment";

import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";
import { matchesSearchQuery } from "@/utils/textSearch";
import reportFullscreenMixin from "@/mixins/reportFullscreenMixin";

export default {
  name: "ReceiptIncomeReport",
  mixins: [reportFullscreenMixin],
  components: {
    Dialog,
    Loading,
    ProcessingFeeTooltip,
  },
  data() {
    return {
      isLoading: false,
      reportTitle: "รายงานรายรับ แยกตามใบเสร็จ",
      reportNameEn: "Income-Receipt",

      dateRange: [],
      receiptStatus: 0,
      receiptStatusOpt: [
        {
          value: null,
          text: "ทั้งหมด",
        },
        {
          value: "0",
          text: "ปกติ",
        },
        {
          value: "1",
          text: "ยกเลิก",
        },
      ],
      //table
      tableBusy: false,
      receiptsList: [],
      fields: [
        {
          key: "index",
          label: "",
        },
        {
          key: "creationDt",
          label: "วันที",
          sortable: true,
          class: "narrow-spacing",
        },
        {
          key: "time",
          label: "เวลา",
          class: "narrow-spacing",
        },
        {
          key: "receiptNo",
          label: "เลขที่ใบเสร็จ",
          class: "narrow-spacing",
        },
        {
          key: "dn",
          label: "DN.",
          class: "narrow-spacing",
        },
        {
          key: "patientFullName",
          label: "ชื่อคนไข้",
          class: "narrow-spacing",
        },
        {
          key: "price",
          label: "ค่ารักษา",
          class: "text-right",
          formatter: "formatNumber",
        },
        {
          key: "finalDiscount",
          label: "ส่วนลด",
          class: "text-right",
          formatter: "formatNumber",
        },
        {
          key: "amount",
          label: "ค่ารักษาสุทธิ",
          class: "text-right",
          formatter: "formatNumber",
        },
        {
          key: "paid",
          label: "ชำระ",
          class: "text-right",
          formatter: "formatNumber",
        },
        {
          key: "overdue",
          label: "ค้างชำระ",
          class: "text-right text-danger",
          formatter: "formatNumber",
        },
      ],
      visibleRows: [],
      excludeKeys: ["price", "finalDiscount", "amount", "overdue"],

      // free-text search across receipt#, patient, DN
      searchTable: "",

      // pagination
      currentPage: 1,
      perPage: 100,

      tableTopPos: null,

      showAllPaymentMethods: false,
      loadingTime: 0,
    };
  },
  created() {
    this.init();
  },
  computed: {
    ...mapGetters({
      userInfo: "moduleUser/getUserInfo",
      getReportDateRange: "moduleReport/getReportDateRange",
      paymentMethodList: "moduleUser/getPaymentMethodList",
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      branchInfo: "moduleBranch/getBranchInfo",
      clinicInfo: "moduleBranch/getClinicInfo",
    }),
    filteredReceiptsList() {
      return this.receiptsList
        .filter(i =>
          matchesSearchQuery(i, this.searchTable, [
            "receiptNo",
            "patientFullName",
            "dn",
          ])
        )
        .slice(0)
        .sort(
          (a, b) =>
            moment(a.creationDt).valueOf() - moment(b.creationDt).valueOf()
        )
        .map((i, index) => {
          let receiptPayment = i.receiptPayment.reduce((acc, curr) => {
            let obj = {};
            obj[curr.id] = curr;
            return Object.assign(acc, obj);
          }, {});
          let _rowVariant = i.cancelFlag ? "secondary" : null;
          return { ...i, receiptPayment, row: index + 1, _rowVariant };
        });
    },
    dynamicFields() {
      let methods = this.paymentMethodList;
      if (!this.showAllPaymentMethods) {
        methods = methods.filter(pm =>
          this.filteredReceiptsList.some(row =>
            row.receiptPayment && row.receiptPayment[pm.id] && row.receiptPayment[pm.id].amount > 0
          )
        );
      }
      let paymentFields = methods.map(i => {
        return {
          key: `receiptPayment.${i.id}.amount`,
          label: i.name,
          class: "text-right",
          thClass: "narrow-spacing",
          processingFee: i.processingFee,
          formatter: "formatNumber",
        };
      });
      let statusField = { key: "status", label: "" };
      let buttonField = {
        key: "button",
        label: "",
        class: "p-0 align-middle",
      };
      return [...this.fields, ...paymentFields, statusField, buttonField];
    },
    dateFrom() {
      return this.dateRange[0]
        ? moment(this.dateRange[0]).startOf("day").toISOString(true)
        : "";
    },
    dateTo() {
      return this.dateRange[1]
        ? moment(this.dateRange[1]).endOf("day").toISOString(true)
        : "";
    },
    moreThanOneDay() {
      if (!this.dateFrom || !this.dateTo) return false;
      const df = moment(this.dateFrom);
      const dt = moment(this.dateTo);

      // Calculate the difference in days between the current date and the given date
      const differenceInDays = dt.diff(df, "days");

      // Check if the difference is more than one day
      // return differenceInDays > 0;
      return false;
    },
    today() {
      let today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    rows() {
      return this.filteredReceiptsList.length;
    },
    tableHeight() {
      if (this.isFullscreen) return "100%";
      return `calc(100vh - ${this.tableTopPos + 40}px)`;
    },
  },
  watch: {
    searchTable() {
      this.currentPage = 1;
    },
  },
  methods: {
    ...mapActions({
      fetchReportBranchReceipt: "moduleReport/fetchReportBranchReceipt",
      emailReportBranchReceipt: "moduleReport/emailReportBranchReceipt",
      setStateReportDateRange: "moduleReport/setStateReportDateRange",
      fetchPaymentMethodList: "moduleUser/fetchPaymentMethodList",
    }),
    init() {
      this.fetchPaymentMethodList({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      })
        .then(res => {
          this.paymentMethods = res.data;
        })
        .finally(() => {
          // this.isLoading = false;
        });
    },
    moment() {
      return moment();
    },
    formatDate(date) {
      if (date) {
        return moment(date).locale("th").format("D MMM YY");
      } else {
        return "";
      }
    },
    formatTime(date) {
      if (date) {
        return moment(date).locale("th").format("HH:mm");
      } else {
        return "";
      }
    },
    formatNumber(value) {
      if (value === null || value === undefined || isNaN(value)) return "";
      return Number(value).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    showDialogToast(variant, textDetail) {
      this.$refs.Dialog.showToast(variant, textDetail);
    },
    sum(key) {
      if (this.excludeKeys.includes(key)) return;
      let splitStr = key.split(".");
      return this.filteredReceiptsList.reduce((accum, item) => {
        let data;
        if (splitStr.length > 1) {
          if (
            item[splitStr[0]] &&
            item[splitStr[0]][splitStr[1]] &&
            item[splitStr[0]][splitStr[1]][splitStr[2]]
          ) {
            data = item[splitStr[0]][splitStr[1]][splitStr[2]];
          } else {
            data = 0;
          }
        } else {
          data = item[key];
        }
        if (typeof data != "number") return null;
        return Math.round((accum + data) * 100) / 100;
      }, null);
    },
    generatePDf() {
      eventBus.$emit(
        "generatePDF",
        this.$refs["ReceiptIncomeTable"].$el,
        this.$refs.header,
        this.$refs.footer
      );
    },

    async sendEmail() {
      try {
        await this.emailReportBranchReceipt({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
          receiptStatus: this.receiptStatus,
        });
        this.$refs.Dialog.showAlertInfo(
          "รายงานจะถูกส่งไปทางอีเมลของท่านภายใน 15 นาที"
        );
      } catch (err) {
        console.error(err);
      }
      //รอ api
    },
    fetchReport() {
      if (this.dateFrom === "" || this.dateTo === "") return;
      this.setStateReportDateRange(this.dateRange);

      this.isLoading = true;
      this.fetchReportBranchReceipt({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        receiptStatus: this.receiptStatus,
      })
        .then(res => {
          this.receiptsList = res.data.report;
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    cancelPopoverConfig(e) {
      return {
        html: true,
        variant: "danger",
        title: () => {
          return `ใบเสร็จ ${e.receiptNo} ถูกยกเลิก`;
        },
        content: () => {
          return `โดย:<em> ${e.cancelFirstName ?? ""} ${
            e.cancelLastName ?? ""
          }</em><br>
          วันเวลา:<em> ${this.formatDate(e.cancelDt) ?? ""} ${
            this.formatTime(e.cancelDt) ?? ""
          }</em><br>
          สาเหตุ:<em> ${e.cancelRemark ?? ""}</em>`;
        },
      };
    },
    async excelExport(id) {
      this.perPage = null;
      await this.$nextTick();

      exportTableToExcel({
        tableId: id,
        reportTitle: this.reportTitle,
        reportNameEn: this.reportNameEn,
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        branchName: this.branchInfo && this.branchInfo.name,
        clinicName: this.clinicInfo && this.clinicInfo.name,
      });

      this.perPage = 100;
    },
    async pdfExport(id) {
      this.perPage = null;
      await this.$nextTick();

      exportReportToPdf({
        tableId: id,
        reportTitle: this.reportTitle,
        reportNameEn: this.reportNameEn,
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        branchName: this.branchInfo && this.branchInfo.name,
        clinicName: this.clinicInfo && this.clinicInfo.name,
      });

      this.perPage = 100;
    },
    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
    openReceipt(row) {
      eventBus.$emit("openReceipt", row);
    },
  },

  async mounted() {
    if (this.getReportDateRange[0] && this.getReportDateRange[1]) {
      this.dateRange = this.getReportDateRange;
    } else {
      this.dateRange = [this.today, this.today];
    }

    // await this.$nextTick();
    this.tableTopPos = document
      .querySelector("#ReceiptIncomeTable")
      .getBoundingClientRect().top;

    eventBus.$on("afterCancelReceipt", e => {
      this.fetchReport();
    });
  },
  updated() {},
};
</script>

<style>
.sr-only {
  display: none;
}
</style>
